// Marketing site vs. application host split.
//
// In production the public marketing page lives on the root domain
// (clozdl.com) and the actual CRM app lives on app.clozdl.com. This module is
// the single source of truth for "which host am I on" and "where do I send the
// user for the app / for marketing".
//
// Hosts come from Vite build args so non-prod (localhost, previews) Just Works:
//   VITE_MARKETING_HOST  e.g. "clozdl.com"
//   VITE_APP_HOST        e.g. "app.clozdl.com"
// When either is unset (local dev), the split is disabled and the app behaves
// as a single-host SPA exactly as before.

const MARKETING_HOST = import.meta.env.VITE_MARKETING_HOST || "";
const APP_HOST = import.meta.env.VITE_APP_HOST || "";

// The split only applies when both hosts are configured AND differ. In dev they
// are blank, so `splitEnabled` is false and every helper degrades to same-host
// behavior.
export const splitEnabled = Boolean(
  MARKETING_HOST && APP_HOST && MARKETING_HOST !== APP_HOST,
);

function currentHost() {
  return typeof window !== "undefined" ? window.location.host : "";
}

// Normalise a host for marketing-host comparison: drop a leading "www." so that
// www.propertymakers.co.in is treated the same as the bare propertymakers.co.in.
function stripWww(host) {
  return host.replace(/^www\./i, "");
}

// True when the browser is on the public marketing host — the bare domain OR its
// www. variant (both must render the marketing site, not the app/login page).
export function onMarketingHost() {
  return splitEnabled && stripWww(currentHost()) === stripWww(MARKETING_HOST);
}

// A host that begins with "app." — the dedicated app host, in both prod
// (app.propertymakers.com) and dev (app.localhost:3000), even when the Vite
// split env vars aren't configured. Lets local dev mirror prod host behaviour.
function hostIsAppSubdomain() {
  return /^app\./i.test(currentHost());
}

// True when on the app host — or always, when the split is off and we're not on
// an app.* host (single-host dev), so app routing stays active locally.
export function onAppHost() {
  if (hostIsAppSubdomain()) return true;
  return !splitEnabled || currentHost() === APP_HOST;
}

// True when we're on the dedicated app host — the real app.<domain> (split
// enabled) OR any app.* host in dev. Used to hide the marketing site on the app
// host so /, /properties, etc. serve the CRM app there.
export function isDedicatedAppHost() {
  if (hostIsAppSubdomain()) return true;
  return splitEnabled && currentHost() === APP_HOST;
}

// Absolute URL to a path on the app host, e.g. appUrl("/dashboard").
// When the split is off, returns the plain path (same-host navigation).
export function appUrl(path = "/") {
  const p = path.startsWith("/") ? path : `/${path}`;
  if (!splitEnabled) return p;
  const protocol =
    typeof window !== "undefined" ? window.location.protocol : "https:";
  return `${protocol}//${APP_HOST}${p}`;
}

// Absolute URL to the marketing host root.
export function marketingUrl(path = "/") {
  const p = path.startsWith("/") ? path : `/${path}`;
  if (!splitEnabled) return p;
  const protocol =
    typeof window !== "undefined" ? window.location.protocol : "https:";
  return `${protocol}//${MARKETING_HOST}${p}`;
}

// Send the browser to a path on the app host (full navigation, crosses origin).
export function goToApp(path = "/dashboard") {
  if (typeof window !== "undefined") window.location.assign(appUrl(path));
}

// Marketing paths owned by the public marketing SPA (home, listings, about).
// Kept in sync with MarketingSite's routes.
const MARKETING_PATHS = [/^\/$/, /^\/properties(\/|$)/, /^\/p\/[^/]+$/, /^\/about$/, /^\/privacypolicy$/];

function isMarketingPath(pathname) {
  return MARKETING_PATHS.some((re) => re.test(pathname));
}

// Whether to render the public marketing SPA (instead of the CRM app) for the
// current request. True when:
//   • we're on the dedicated marketing host (prod: propertymakers.com), OR
//   • the split is off (dev single-host) AND the path is a marketing path —
//     so /login, /dashboard, /leads, etc. still reach the app in dev.
// The dedicated app host (app.propertymakers.com) never renders marketing.
export function shouldRenderMarketing(pathname = window.location.pathname) {
  if (isDedicatedAppHost()) return false;
  if (onMarketingHost()) return true;
  return !splitEnabled && isMarketingPath(pathname);
}
